import { pickedFile } from '../../utils/pickedFile.js'
import MediaCaptureField from '../common/MediaCaptureField.js'
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import { DashboardLayout } from '../common/page_component/index.js';
import DocumentModal from '../common/DocumentModal';
import Alert from '../common/Alert.js';
import Loader from '../common/Loader.js';
import companyService from '../../services/CompanyService.js';
import companySubscriptionService from '../../services/CompanySubscriptionService.js';
import { CreditCard, Calendar, DollarSign, FileText, Camera, Plus, Trash2, Eye, Download } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import { companySelectLabel } from '../../utils/companyFeatures.js';
import { useTranslation } from 'react-i18next';
import { downloadBlob } from '../../utils/downloadBlob.js';


const EditCompanySubscription = () => {
  const { t } = useTranslation();
  const { subscriptionId } = useParams();
  const [activeTab, setActiveTab] = useState('subscription');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [openPaymentActionId, setOpenPaymentActionId] = useState(null);
  const paymentActionRef = useRef(null);
  
  // Subscription form data
  const [subscriptionData, setSubscriptionData] = useState({
    company_id: '',
    period_type: 'one_month',
    custom_years: '',
    from_date: '',
    to_date: '',
    subscription_amount: '',
    notes: '',
    status: 'active'
  });

  // Payment form data for adding new payment
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    payment_status: 'completed',
    payment_date: new Date().toISOString().split('T')[0],
    transaction_id: '',
    transaction_reference: '',
    bank_name: '',
    cheque_number: '',
    upi_id: '',
    notes: '',
    remarks: ''
  });

  const [receipt, setReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const navigate = useNavigate();

  const { errors: subErrors, validate: validateSub, validateField: validateSubField } = useValidation();
  const { errors: payErrors, validate: validatePay, validateField: validatePayField } = useValidation();

  const subRules = {
    company_id:          (v) => validators.select(v, 'company'),
    from_date:           (v) => validators.required(v, 'From date'),
    subscription_amount: (v) => validators.required(v, 'Subscription amount') || validators.number(v, { min: 0.01, label: 'Subscription amount' }),
    custom_years:        (v) => subscriptionData.period_type === 'custom_years' ? (validators.required(v, 'Custom years') || validators.number(v, { min: 1, label: 'Custom years' })) : '',
  };

  const payRules = {
    amount:       (v) => validators.required(v, 'Amount') || validators.number(v, { min: 0.01, label: 'Amount' }),
    payment_date: (v) => validators.required(v, 'Payment date'),
  };

  useEffect(() => {
    fetchCompanies();
    fetchSubscription();
    fetchPayments();
  }, [subscriptionId]);

  useEffect(() => {
    if (subscriptionData.from_date && subscriptionData.period_type) {
      // Convert custom_years to integer, or null if empty/invalid
      const customYearsValue = subscriptionData.custom_years 
        ? parseInt(subscriptionData.custom_years) 
        : null;
      
      const endDate = companySubscriptionService.calculateEndDate(
        subscriptionData.from_date,
        subscriptionData.period_type,
        customYearsValue
      );
      setSubscriptionData(prev => ({ ...prev, to_date: endDate }));
    }
  }, [subscriptionData.from_date, subscriptionData.period_type, subscriptionData.custom_years]);

  useEffect(() => {
    if (!openPaymentActionId) return undefined;
    const handleClickOutside = (event) => {
      if (paymentActionRef.current && !paymentActionRef.current.contains(event.target)) {
        setOpenPaymentActionId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openPaymentActionId]);

  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllForSelect();
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Error fetching companies:', err);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await companySubscriptionService.getById(subscriptionId);
      const sub = response.data;
      setSubscriptionData({
        company_id: sub.company_id,
        period_type: sub.period_type,
        custom_years: sub.custom_years || '',
        from_date: sub.from_date,
        to_date: sub.to_date,
        subscription_amount: sub.subscription_amount,
        notes: sub.notes || '',
        status: sub.status
      });
    } catch (err) {
      Alert.error('Failed to load subscription details');
      console.error(err);
      navigate('/company-subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = async () => {
    try {
      setPaymentsLoading(true);
      const response = await companySubscriptionService.getPaymentsBySubscription(subscriptionId);
      setPayments(response.data || []);
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleSubscriptionChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'subscription_amount') filtered = inputFilters.amount(value);
    else if (name === 'custom_years') filtered = inputFilters.digitsOnly(value);

    setSubscriptionData(prev => ({ ...prev, [name]: filtered }));

    if (subRules[name]) {
      validateSubField(name, filtered, subRules[name]);
    }
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'amount') filtered = inputFilters.amount(value);

    setPaymentData(prev => ({ ...prev, [name]: filtered }));

    if (payRules[name]) {
      validatePayField(name, filtered, payRules[name]);
    }
  };

  const handleReceiptChange = (e) => {
    const file = pickedFile(e);
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Receipt file must be less than 5MB');
        return;
      }
      setReceipt(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeReceipt = () => {
    setReceipt(null);
    setReceiptPreview(null);
    const fileInput = document.getElementById('receipt-input');
    if (fileInput) fileInput.value = '';
  };

  const openDocumentModal = (url) => {
  setSelectedDoc(url);
  setIsModalOpen(true);
  };
  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();

    if (!validateSub(subscriptionData, subRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    setSubmitting(true);

    try {
      const dataToSend = {
        ...subscriptionData,
        subscription_amount: parseFloat(subscriptionData.subscription_amount),
        custom_years: subscriptionData.period_type === 'custom_years' ? parseInt(subscriptionData.custom_years) : null
      };

      await companySubscriptionService.update(subscriptionId, dataToSend);
      Alert.success('Subscription updated successfully!');
      setTimeout(() => navigate(`/company-subscriptions/view/${subscriptionId}`), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();

    if (!validatePay(paymentData, payRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    setSubmitting(true);

    try {
      const dataToSend = {
        ...paymentData,
        subscription_id: subscriptionId,
        company_id: subscriptionData.company_id,
        amount: parseFloat(paymentData.amount)
      };

      await companySubscriptionService.createPayment(dataToSend, receipt);
      Alert.success('Payment added successfully!');
      
      // Reset form
      setPaymentData({
        amount: '',
        payment_method: 'cash',
        payment_status: 'completed',
        payment_date: new Date().toISOString().split('T')[0],
        transaction_id: '',
        transaction_reference: '',
        bank_name: '',
        cheque_number: '',
        upi_id: '',
        notes: '',
        remarks: ''
      });
      removeReceipt();
      setShowAddPayment(false);
      
      // Refresh payments
      fetchPayments();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to add payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePayment = async (paymentId, paymentName) => {
    const confirmMessage = `Are you sure you want to delete payment "${paymentName}"? This action cannot be undone.`;
    
    const confirmed = await Alert.confirm(confirmMessage, 'Delete Payment');
    
    if (confirmed) {
      try {
        await companySubscriptionService.deletePayment(paymentId);
        Alert.success('Payment deleted successfully!');
        fetchPayments();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete payment');
      }
    }
  };

  const handleDownloadReceipt = async (paymentId) => {
    try {
      const blob = await companySubscriptionService.downloadReceipt(paymentId);
      await downloadBlob(blob, `receipt_${paymentId}.pdf`, 'application/pdf');
    } catch (err) {
      Alert.error('Failed to download receipt');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      completed: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700',
      refunded: 'bg-blue-100 text-blue-700',
      cancelled: 'bg-gray-100 text-gray-700'
    };

    const colorClass = statusConfig[status?.toLowerCase()] || statusConfig.pending;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${colorClass}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase()}
      </span>
    );
  };

  const totalPaid = payments
    .filter(p => p.payment_status === 'completed')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  return (
    <DashboardLayout>
      {(loading || loadingCompanies) ? (
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading subscription details..." />
        </div>
      ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 md:p-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-gray-200">
                  <div className="p-2 bg-orange-100 rounded-xl">
                    <CreditCard className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-700">Edit Subscription</h2>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Update subscription details and manage payments</p>
                  </div>
                  
                </div>

                {/* Tabs */}
                <div className="mb-6">
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                      <button
                        onClick={() => setActiveTab('subscription')}
                        className={`${
                          activeTab === 'subscription'
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                      >
                        <Calendar className="w-4 h-4" />
                        Subscription Details
                      </button>
                      <button
                        onClick={() => setActiveTab('payments')}
                        className={`${
                          activeTab === 'payments'
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                      >
                        <DollarSign className="w-4 h-4" />
                        Payment Management ({payments.length})
                      </button>
                    </nav>
                  </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'subscription' && (
                  <form onSubmit={handleSubscriptionSubmit} className="space-y-6">
                    {/* Subscription Details */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Subscription Information</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Company <span className="text-danger font-bold">*</span>
                          </label>
                          <SelectField variant="form"
                            name="company_id"
                            value={subscriptionData.company_id}
                            onChange={handleSubscriptionChange}
                            className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base bg-gray-50 ${subErrors.company_id ? 'border-red-400' : 'border-gray-300'}`}
                            required
                            disabled
                          >
                            <option value="">Select Company</option>
                            {companies.map(company => (
                              <option key={company.id} value={company.id}>
                                {companySelectLabel(company)}
                              </option>
                            ))}
                          </SelectField>
                          <ValidationMessage error={subErrors.company_id} />
                          <p className="mt-1 text-xs text-gray-500">Company cannot be changed</p>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Period Type <span className="text-danger font-bold">*</span>
                          </label>
                          <SelectField variant="form"
                            name="period_type"
                            value={subscriptionData.period_type}
                            onChange={handleSubscriptionChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            required
                          >
                            <option value="seven_days">7 Days</option>
                            <option value="one_month">1 Month</option>
                            <option value="three_months">3 Months</option>
                            <option value="six_months">6 Months</option>
                            <option value="twelve_months">12 Months</option>
                            <option value="custom_years">Custom Years</option>
                          </SelectField>
                        </div>

                        {subscriptionData.period_type === 'custom_years' && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Custom Years <span className="text-danger font-bold">*</span>
                            </label>
                            <input
                              type="text"
                              name="custom_years"
                              value={subscriptionData.custom_years}
                              onChange={handleSubscriptionChange}
                              placeholder="Enter number of years"
                              className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${subErrors.custom_years ? 'border-red-400' : 'border-gray-300'}`}
                              required
                            />
                            <ValidationMessage error={subErrors.custom_years} />
                          </div>
                        )}

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            From Date <span className="text-danger font-bold">*</span>
                          </label>
                          <input
                            type="date"
                            name="from_date"
                            value={subscriptionData.from_date}
                            onChange={handleSubscriptionChange}
                            className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${subErrors.from_date ? 'border-red-400' : 'border-gray-300'}`}
                            required
                          />
                          <ValidationMessage error={subErrors.from_date} />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            To Date <span className="text-danger font-bold">*</span>
                          </label>
                          <input
                            type="date"
                            name="to_date"
                            value={subscriptionData.to_date}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base"
                            readOnly
                          />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Subscription Amount <span className="text-danger font-bold">*</span>
                          </label>
                          <input
                            type="text"
                            name="subscription_amount"
                            value={subscriptionData.subscription_amount}
                            onChange={handleSubscriptionChange}
                            placeholder="Enter amount"
                            className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${subErrors.subscription_amount ? 'border-red-400' : 'border-gray-300'}`}
                            required
                          />
                          <ValidationMessage error={subErrors.subscription_amount} />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Status <span className="text-danger font-bold">*</span>
                          </label>
                          <SelectField variant="form"
                            name="status"
                            value={subscriptionData.status}
                            onChange={handleSubscriptionChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            required
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </SelectField>
                        </div>

                        <div className="col-span-2 sm:col-span-2 lg:col-span-3">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Notes
                          </label>
                          <textarea
                            name="notes"
                            value={subscriptionData.notes}
                            onChange={handleSubscriptionChange}
                            rows="3"
                            placeholder="Enter any additional notes"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Payment Summary */}
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                      <h4 className="text-sm font-semibold text-blue-900 mb-2">Payment Summary</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                        <div>
                          <p className="text-xs text-blue-700">Subscription Amount</p>
                          <p className="text-lg font-bold text-blue-900">
                            {formatCurrency(subscriptionData.subscription_amount)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-blue-700">Total Paid</p>
                          <p className="text-lg font-bold text-green-700">{formatCurrency(totalPaid)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-blue-700">Balance</p>
                          <p className={`text-lg font-bold ${subscriptionData.subscription_amount - totalPaid > 0 ? 'text-red-700' : 'text-green-700'}`}>
                            {formatCurrency(subscriptionData.subscription_amount - totalPaid)}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 border-t">
                      <Button variant="primary" type="submit" loading={submitting}>
                        {submitting ? 'Updating...' : 'Update Subscription'}
                      </Button>
                      <Button variant="secondary" onClick={() => navigate(`/company-subscriptions/view/${subscriptionId}`)}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}

                {activeTab === 'payments' && (
                  <div className="space-y-6">
                    {/* Add Payment Button */}
                    <div className="flex justify-between items-center">
                      <h3 className="text-base sm:text-md font-semibold text-gray-700">
                        Payment History ({payments.length})
                      </h3>
                      <Button variant="primary" icon={Plus} onClick={() => setShowAddPayment(!showAddPayment)}>
                        Add Payment
                      </Button>
                    </div>

                    {/* Add Payment Form */}
                    {showAddPayment && (
                      <form onSubmit={handleAddPayment} className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                        <h4 className="text-sm font-semibold text-gray-900 mb-4">Add New Payment</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Amount <span className="text-danger font-bold">*</span>
                            </label>
                            <input
                              type="text"
                              name="amount"
                              value={paymentData.amount}
                              onChange={handlePaymentChange}
                              placeholder="Enter amount"
                              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm ${payErrors.amount ? 'border-red-400' : 'border-gray-300'}`}
                              required
                            />
                            <ValidationMessage error={payErrors.amount} />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Payment Method <span className="text-danger font-bold">*</span>
                            </label>
                            <SelectField variant="form"
                              name="payment_method"
                              value={paymentData.payment_method}
                              onChange={handlePaymentChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                              required
                            >
                              <option value="cash">Cash</option>
                              <option value="bank_transfer">Bank Transfer</option>
                              <option value="cheque">Cheque</option>
                              <option value="upi">UPI</option>
                              <option value="credit_card">Credit Card</option>
                              <option value="debit_card">Debit Card</option>
                              <option value="net_banking">Net Banking</option>
                              <option value="other">Other</option>
                            </SelectField>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Payment Status <span className="text-danger font-bold">*</span>
                            </label>
                            <SelectField variant="form"
                              name="payment_status"
                              value={paymentData.payment_status}
                              onChange={handlePaymentChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                              required
                            >
                              <option value="pending">Pending</option>
                              <option value="completed">Completed</option>
                              <option value="failed">Failed</option>
                            </SelectField>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Payment Date <span className="text-danger font-bold">*</span>
                            </label>
                            <input
                              type="date"
                              name="payment_date"
                              value={paymentData.payment_date}
                              onChange={handlePaymentChange}
                              className={`w-full px-3 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm ${payErrors.payment_date ? 'border-red-400' : 'border-gray-300'}`}
                              required
                            />
                            <ValidationMessage error={payErrors.payment_date} />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Transaction ID</label>
                            <input
                              type="text"
                              name="transaction_id"
                              value={paymentData.transaction_id}
                              onChange={handlePaymentChange}
                              placeholder="Enter transaction ID"
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                            />
                          </div>

                          <div className="col-span-2 sm:col-span-2 lg:col-span-3">
                            <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                            <textarea
                              name="notes"
                              value={paymentData.notes}
                              onChange={handlePaymentChange}
                              rows="2"
                              placeholder="Enter payment notes"
                              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                            />
                          </div>

                          <div className="col-span-2 sm:col-span-2 lg:col-span-3">
                            <label className="block text-xs font-medium text-gray-700 mb-2">Upload Receipt</label>
                            <MediaCaptureField mode="document" onPick={handleReceiptChange} />
                          
                            <label
  htmlFor="receipt-input"
  className="w-40 h-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
>
  {!receiptPreview ? (
    <div className="text-center">
      <Camera className="mx-auto w-8 h-8 text-gray-400 mb-2" />
      <p className="text-xs text-gray-500">Click to upload</p>
    </div>
  ) : receipt?.type?.startsWith('image/') ? (
    <img
      src={receiptPreview}
      alt="Receipt"
      className="w-full h-full object-contain rounded-lg"
      onClick={(e) => {
        e.preventDefault();
        openDocumentModal(receiptPreview);
      }}
    />
  ) : (
    <div
      className="flex flex-col items-center justify-center text-center px-2"
      onClick={(e) => {
        e.preventDefault();
        openDocumentModal(receiptPreview);
      }}
    >
      <FileText className="w-8 h-8 text-gray-500 mb-2" />
      <p className="text-xs text-gray-600 break-all">
        {receipt?.name}
      </p>
    </div>
  )}

  {receiptPreview && (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        removeReceipt();
      }}
      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
    >
      ✕
    </button>
  )}
</label>
</div>

                        <div className="flex gap-3 mt-4">
                          <Button variant="success" type="submit" loading={submitting}>
                            {submitting ? 'Adding...' : 'Add Payment'}
                          </Button>
                          <Button variant="secondary" onClick={() => {
                              setShowAddPayment(false);
                              setPaymentData({
                                amount: '',
                                payment_method: 'cash',
                                payment_status: 'completed',
                                payment_date: new Date().toISOString().split('T')[0],
                                transaction_id: '',
                                transaction_reference: '',
                                bank_name: '',
                                cheque_number: '',
                                upi_id: '',
                                notes: '',
                                remarks: ''
                              });
                              removeReceipt();
                            }}>
                            Cancel
                          </Button>
                        </div>
                        </div>
                      </form>
                    )}

                    {/* Payments List */}
                    {paymentsLoading ? (
                      <div className="text-center py-8">
                        <Loader size="small" message="Loading payments..." />
                      </div>
                    ) : payments.length === 0 ? (
                      <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
                        <Camera className="mx-auto w-8 h-8 text-gray-400 mb-2" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No payments recorded</h3>
                        <p className="mt-1 text-sm text-gray-500">Add your first payment to this subscription</p>
                      </div>
                    ) : (
                      <>
                      <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {payments.map((payment) => (
                              <tr key={payment.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {payment.payment_id}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {formatDate(payment.payment_date)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                                  {formatCurrency(payment.amount)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                  {companySubscriptionService.getPaymentMethodDisplayName(payment.payment_method)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {getPaymentStatusBadge(payment.payment_status)}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap">
                                  {payment.has_receipt ? (
                                    <button
                                      onClick={() => handleDownloadReceipt(payment.id)}
                                      className="text-blue-600 hover:text-blue-900 transition-colors flex items-center gap-1"
                                      title="Download Receipt"
                                    >
                                      <Download className="w-4 h-4" />
                                      <span className="text-xs">Download</span>
                                    </button>
                                  ) : (
                                    <span className="text-xs text-gray-400">No receipt</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                                  <button
                                    onClick={() => handleDeletePayment(payment.id, payment.payment_id)}
                                    className="text-red-600 hover:text-red-900 transition-colors"
                                    title="Delete Payment"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="md:hidden space-y-3">
                        {payments.map((payment) => (
                          <div
                            key={payment.id}
                            className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-900 truncate">{payment.payment_id}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{formatDate(payment.payment_date)}</p>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                {getPaymentStatusBadge(payment.payment_status)}
                                <RowActionsMenu
                                  isOpen={openPaymentActionId === payment.id}
                                  onToggle={() =>
                                    setOpenPaymentActionId(
                                      openPaymentActionId === payment.id ? null : payment.id
                                    )
                                  }
                                  onClose={() => setOpenPaymentActionId(null)}
                                  menuRef={paymentActionRef}
                                  onDelete={() => handleDeletePayment(payment.id, payment.payment_id)}
                                  deleteLabel="Delete Payment"
                                  extraItems={
                                    payment.has_receipt
                                      ? [
                                          {
                                            label: 'Download Receipt',
                                            icon: '📥',
                                            onClick: () => handleDownloadReceipt(payment.id),
                                          },
                                        ]
                                      : []
                                  }
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-gray-500">Amount:</span>
                                <p className="font-semibold text-gray-900 mt-0.5">{formatCurrency(payment.amount)}</p>
                              </div>
                              <div>
                                <span className="text-gray-500">Method:</span>
                                <p className="font-medium text-gray-900 mt-0.5">
                                  {companySubscriptionService.getPaymentMethodDisplayName(payment.payment_method)}
                                </p>
                              </div>
                              <div className="col-span-2">
                                <span className="text-gray-500">Receipt:</span>
                                <p className="mt-0.5">
                                  {payment.has_receipt ? (
                                    <button
                                      type="button"
                                      onClick={() => handleDownloadReceipt(payment.id)}
                                      className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                    >
                                      Download
                                    </button>
                                  ) : (
                                    <span className="text-gray-400">No receipt</span>
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      </>
                    )}
                  </div>
                )}
              </div>
      )}
      <DocumentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        documentUrl={selectedDoc}
      />
    </DashboardLayout>
  );

  };

export default EditCompanySubscription;